import React from 'react';
import { Button, Space, Tag, Typography } from 'antd';
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  CloseOutlined,
  EyeOutlined,
  HolderOutlined,
} from '@ant-design/icons';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { moveItem, type EditorWord } from '../lib/words';

interface RowProps {
  word: EditorWord;
  index: number;
  count: number;
  disabled: boolean;
  onMove: (from: number, to: number) => void;
  onRemove: (key: string) => void;
  onView?: (word: EditorWord) => void;
}

const Row: React.FC<RowProps> = ({ word, index, count, disabled, onMove, onRemove, onView }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: word.key, disabled });

  return (
    <li
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '8px 12px',
        background: isDragging ? '#eef0ff' : '#fff',
        borderBottom: '1px solid #eef0f6',
        position: 'relative',
        zIndex: isDragging ? 1 : 0,
        listStyle: 'none',
      }}
    >
      <Button
        type="text"
        size="small"
        ref={setActivatorNodeRef}
        icon={<HolderOutlined />}
        aria-label={`Drag to reorder ${word.lemma}`}
        disabled={disabled}
        style={{ cursor: disabled ? 'not-allowed' : 'grab', touchAction: 'none' }}
        {...attributes}
        {...listeners}
      />
      <Typography.Text type="secondary" style={{ width: 36, textAlign: 'right', flexShrink: 0 }}>
        {index + 1}
      </Typography.Text>
      <div style={{ flex: 1, minWidth: 0 }}>
        <Typography.Text strong>{word.lemma}</Typography.Text>
        {word.context && (
          <Typography.Text type="secondary" ellipsis style={{ display: 'block' }}>
            {word.context}
          </Typography.Text>
        )}
      </div>
      {word.ready === undefined ? (
        <Tag color="blue">New</Tag>
      ) : word.ready ? (
        <Tag color="green">Card ready</Tag>
      ) : (
        <Tag color="orange">No card yet</Tag>
      )}
      <Space size={2}>
        {onView && (
          <Button
            type="text"
            size="small"
            icon={<EyeOutlined />}
            aria-label={`View the card of ${word.lemma}`}
            title={word.ready ? 'View card' : 'No card to view yet'}
            disabled={word.ready !== true}
            onClick={() => onView(word)}
          />
        )}
        <Button
          type="text"
          size="small"
          icon={<ArrowUpOutlined />}
          aria-label={`Move ${word.lemma} up`}
          disabled={disabled || index === 0}
          onClick={() => onMove(index, index - 1)}
        />
        <Button
          type="text"
          size="small"
          icon={<ArrowDownOutlined />}
          aria-label={`Move ${word.lemma} down`}
          disabled={disabled || index === count - 1}
          onClick={() => onMove(index, index + 1)}
        />
        <Button
          type="text"
          size="small"
          danger
          icon={<CloseOutlined />}
          aria-label={`Remove ${word.lemma}`}
          disabled={disabled}
          onClick={() => onRemove(word.key)}
        />
      </Space>
    </li>
  );
};

/**
 * The deck's ordered words. Reorder by dragging the handle (pointer or keyboard: Space to pick
 * up, arrow keys, Space to drop) or with the up/down buttons, which also suit long lists.
 */
export const WordList: React.FC<{
  words: EditorWord[];
  onChange: (words: EditorWord[]) => void;
  disabled?: boolean;
  /** Opens the card of a word that has one (the eye button). */
  onView?: (word: EditorWord) => void;
}> = ({ words, onChange, disabled = false, onView }) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const onMove = (from: number, to: number) => onChange(moveItem(words, from, to));
  const onRemove = (key: string) => onChange(words.filter((w) => w.key !== key));

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = words.findIndex((w) => w.key === active.id);
    const to = words.findIndex((w) => w.key === over.id);
    if (from !== -1 && to !== -1) onChange(moveItem(words, from, to));
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={words.map((w) => w.key)} strategy={verticalListSortingStrategy}>
        <ol
          aria-label="Words in this deck"
          style={{
            margin: 0,
            padding: 0,
            border: '1px solid #e8e9f3',
            borderRadius: 8,
            overflow: 'hidden',
          }}
        >
          {words.map((w, i) => (
            <Row
              key={w.key}
              word={w}
              index={i}
              count={words.length}
              disabled={disabled}
              onMove={onMove}
              onRemove={onRemove}
              onView={onView}
            />
          ))}
        </ol>
      </SortableContext>
    </DndContext>
  );
};
